import React, { useRef, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { Link, navigate } from "gatsby";
import classNames from "classnames";
import states from "states-us";

import InvisibleReCAPTCHA from "./InvisibleReCAPTCHA";

import { useSiteMetadata } from "../hooks/useSiteMetadata";
import {
    getFormattedPhoneNumber,
    isValidEmail,
    isValidName,
    isValidPhoneNumber,
} from "../utils/utils";

const CMSDashboardAccessForm = () => {
    const [facilityName, setFacilityName] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [contactInfo, setContactInfo] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [privacyCheckbox, setPrivacyCheckbox] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);
    const [errors, setErrors] = useState({});
    const [submissionError, setSubmissionError] = useState(null);

    const setValidity = (field, valid) =>
        setErrors({ ...errors, [field]: !valid });

    const updateContactInfo = e => {
        setContactInfo(e.target.value);
        setValidity("contactInfo", isValidEmail(e.target.value));
    };

    const updatePhoneNumber = e => {
        let number = getFormattedPhoneNumber(e.target.value);
        setPhoneNumber(number);
        setValidity("phoneNumber", isValidPhoneNumber(number));
    };

    const isValid = () => {
        const _errors = {
            facilityName: !isValidName(facilityName),
            city: !isValidName(city),
            state: !state,
            contactInfo: !isValidEmail(contactInfo),
            phoneNumber: !isValidPhoneNumber(phoneNumber),
        };

        setErrors(_errors);
        return !Object.keys(_errors).some(key => _errors[key]);
    };

    const {
        endpoints: { postAcuteLead: endpointUrl },
    } = useSiteMetadata();

    const postFormData = async reCAPTCHAToken => {
        const formData = {
            facility_name: facilityName,
            city,
            state,
            contact_info: contactInfo,
            phone: phoneNumber,
            recaptcha_token: reCAPTCHAToken,
            referer: window?.location?.href || "",
        };

        const response = await fetch(endpointUrl, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
        });
        return response.json();
    };

    const submitForm = reCAPTCHAToken => {
        setSubmitting(true);
        if (reCAPTCHAToken && isValid()) {
            postFormData(reCAPTCHAToken)
                .then(data => {
                    setSubmissionError(data.error ? data.msg : null);
                    setSubmitted(true);
                })
                .catch(error => console.error(error));
        } else {
            setHasSubmitAttempt(true);
        }
        reCAPTCHARef.current.reset();
        setSubmitting(false);
    };

    const reCAPTCHARef = useRef(null);

    const executeReCAPTCHA = async e => {
        e.preventDefault();
        try {
            const token = await reCAPTCHARef.current.executeAsync();
            submitForm(token);
        } catch (error) {
            console.error(error);
        }
    };

    if (submitted && !submissionError) {
        navigate("/staffing-mandate-dashboard/");
    }

    return (
        <Form
            key="cms-dashboard-access-form"
            className={classNames("cms-dashboard-access-form", {
                "has-submit-attempt": hasSubmitAttempt,
            })}
            onSubmit={executeReCAPTCHA}
        >
            <Row>
                <Col xs={12} className="mb-4">
                    <Form.Control
                        type="text"
                        size="lg"
                        required
                        value={facilityName}
                        onChange={e => setFacilityName(e.target.value)}
                        placeholder="*Facility Name"
                        className={classNames({
                            invalid: !!errors["facilityName"],
                        })}
                    />
                </Col>

                <Col md={6} className="mb-4">
                    <Form.Control
                        type="text"
                        size="lg"
                        required
                        value={city}
                        onChange={e => setCity(e.target.value)}
                        placeholder="*City"
                        className={classNames({
                            invalid: !!errors["city"],
                        })}
                    />
                </Col>

                <Col md={6} className="mb-4">
                    <Form.Select
                        required
                        size="lg"
                        defaultValue={state}
                        onChange={e => setState(e.target.value)}
                        className={classNames({
                            "select-placeholder": !state,
                            invalid: !!errors["state"],
                        })}
                    >
                        <option disabled value="">
                            *State
                        </option>
                        {states.map(state => (
                            <option
                                value={state.abbreviation}
                                key={state.abbreviation}
                            >
                                {state.name}
                            </option>
                        ))}
                    </Form.Select>
                </Col>

                <Col md={6} className="mb-4">
                    <Form.Control
                        type="email"
                        size="lg"
                        required
                        value={contactInfo}
                        onChange={updateContactInfo}
                        placeholder="*Contact Email"
                        className={classNames({
                            invalid: !!errors["contactInfo"],
                        })}
                    />
                </Col>

                <Col md={6} className="mb-4">
                    <Form.Control
                        type="tel"
                        maxLength="14"
                        placeholder="*Phone Number"
                        size="lg"
                        value={phoneNumber}
                        onChange={updatePhoneNumber}
                        className={classNames({
                            invalid: !!errors["phoneNumber"],
                        })}
                    />
                </Col>

                <Col xs={12} className="mb-4">
                    <Form.Check size="lg" type="checkbox" className="ms-1">
                        <Form.Check.Input
                            type="checkbox"
                            required
                            aria-label="Disclaimer"
                            checked={privacyCheckbox}
                            onChange={e => setPrivacyCheckbox(e.target.checked)}
                        />
                        <Form.Check.Label className="compliance ms-1">
                            I agree to receive promotional and transactional
                            messages from ShiftMed. Our privacy policy can be
                            found <Link to="/privacy/">here</Link>.
                        </Form.Check.Label>
                    </Form.Check>
                </Col>

                <Col md={3}>
                    <Button
                        variant="primary"
                        type="submit"
                        size="lg"
                        className="w-100"
                        disabled={submitting}
                    >
                        Submit
                    </Button>
                </Col>
            </Row>

            {submissionError && (
                <Alert className="my-3" variant="danger">
                    {submissionError}
                </Alert>
            )}
            <InvisibleReCAPTCHA innerRef={reCAPTCHARef} />
        </Form>
    );
};

export default CMSDashboardAccessForm;
